// src/components/CustomerLogo.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDataLayer, useCachedHierarchy } from '@quantiix/data-layer';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Customer (tenant) branding logo, fixed in the top-right corner.
 *
 * Source: the active company's logo URL, delivered by the BE on the company
 * document and surfaced through the cached hierarchy (hierarchy.logoUrl).
 * Read the same way the Navbar reads the hierarchy:
 *   targetCompanyId = activeCompanyId || primaryCompany?.id  ->  useCachedHierarchy
 *
 * A company with no logo renders NOTHING. There used to be a bundled 3D Signals
 * mark as a stand-in, which meant every unbranded tenant silently wore someone
 * else's brand — worse than an empty corner. The component is `position: fixed`,
 * so an absent mark costs no layout space either way.
 */

const CustomerLogo = () => {
    const { isAuthenticated, primaryCompany } = useAuth();
    const { companyId: activeCompanyId } = useDataLayer();
    const { pathname } = useLocation();

    // Routes where this floating mark must not appear:
    //  - /app/operator/*       → the machine MFE renders the logo inline in its header
    //  - /login, /unauthorized → auth screens have their own branding (and a stale
    //    session can leave isAuthenticated=true after a browser back-nav)
    const HIDDEN_PREFIXES = ['/app/operator'];
    const HIDDEN_EXACT = ['/login', '/unauthorized'];
    const isHiddenRoute =
        HIDDEN_PREFIXES.some((p) => pathname.startsWith(p)) || HIDDEN_EXACT.includes(pathname);

    // Show only at the top of the page — hide once the user scrolls down so the
    // mark doesn't hover over scrolling content. Capture phase catches scrolling
    // in any container (the admin layout scrolls inside `.main`, not the window).
    const [atTop, setAtTop] = useState(true);

    useEffect(() => { setAtTop(true); }, [pathname]); // a new route starts at the top

    useEffect(() => {
        const onScroll = (e) => {
            const el = e.target;
            const top = (el === document || el === window)
                ? (window.scrollY || document.documentElement.scrollTop || 0)
                : (el.scrollTop ?? 0);
            setAtTop(top <= 8);
        };
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, []);

    // Same company resolution the Navbar uses.
    const targetCompanyId = activeCompanyId || primaryCompany?.id;

    // useCachedHierarchy returns null before the Dashboard has populated the
    // cache — guard explicitly (default params only catch undefined, not null).
    const hierarchy = useCachedHierarchy(targetCompanyId);

    // BE may send snake_case; accept either. No logo → nothing rendered.
    const logoUrl = hierarchy?.logoUrl ?? hierarchy?.logo_url ?? null;

    // Hide before auth, on hidden routes, when there's no logo, or once scrolled.
    if (!isAuthenticated || isHiddenRoute || !logoUrl || !atTop) return null;

    return (
        <div
            style={{
                position: 'fixed',
                // Horizontal line: match the Quantiix navbar logo (.navbarLogo
                // top: var(--ds-spacing-lg), height: 36px) — same top + same
                // band height centers the two logos on the same horizontal line.
                top: 'var(--ds-spacing-lg)',
                height: 36,
                // Vertical line: match the Q-ai launcher (.ds-sfai__fab right: 24px).
                right: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                // Below overlay MFE drawers (z-index 1000, full-screen, mounted
                // only while open) so the branding mark never overlaps a drawer's
                // header controls (e.g. the close X). Stays visible otherwise.
                zIndex: 900,
                // Branding mark must not intercept clicks on MFE controls beneath it.
                pointerEvents: 'none',
            }}
            aria-hidden="true"
        >
            <img
                src={logoUrl}
                alt={hierarchy?.name ? `${hierarchy.name} logo` : 'Customer logo'}
                style={{ height: 32, width: 'auto', display: 'block' }}
            />
        </div>
    );
};

export default CustomerLogo;